<template>
    <div v-if="matches.length" class="allergy-warning" data-testid="allergy-warning">
        <div class="allergy-warning-icon">
            <i class="fa fa-exclamation-triangle" />
        </div>
        <div class="allergy-warning-body flex-fill">
            <div class="allergy-warning-title">
                Allergy Alert &mdash; confirm with the patient before continuing
            </div>
            <div v-for="match in matches" :key="matchKey(match)" class="allergy-warning-match">
                <strong>{{ match.matchedOn }}</strong>
                <span v-if="match.reaction"> &mdash; reaction: {{ match.reaction }}</span>
                <div class="allergy-warning-detail">
                    {{ describe(match) }}
                </div>
            </div>
        </div>
    </div>
    <div v-else-if="unscreened" class="allergy-unscreened" data-testid="allergy-unscreened">
        <i class="fa fa-info-circle" />
        Not screened for allergies &mdash; check this drug against the patient's profile manually.
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct';
import { AllergyScreeningMatch, AllergyType } from '@/models/AllergyScreening';
import DrugService from '@/services/DrugService';

/**
 * Inline allergy conflict warning for the drug section of a prescription.
 *
 * Screens the selected drug against the patient's recorded allergies as soon as it is chosen, so the
 * technician sees the conflict during data entry rather than the pharmacist catching it at verification.
 *
 * Three states, because "no conflict" and "never checked" must not look alike: a conflict renders the red
 * alert, an un-screened drug renders a muted note, and a clean screen renders nothing. Renders nothing by
 * default, so it can be dropped in unconditionally.
 */
@Component({ name: "AllergyWarningComponent" })
export default class AllergyWarningComponent extends Vue {
    @Prop({ type: Number }) protected patientId?: number | null;
    @Prop({ type: Object }) protected drug?: DrugProduct | null;

    protected matches: AllergyScreeningMatch[] = [];

    /**
     * True when a drug is selected but no allergy verdict could be reached — a NonGSDD product, a compound
     * with no GSDD ingredients, or a GSDD failure. Distinct from a clean screen: the service reports these
     * separately and collapsing them here would present "never checked" as "no conflict".
     */
    protected unscreened = false;

    /**
     * Guards against a slow response for a previously selected drug overwriting the result for the drug now
     * on screen — showing an allergy warning against the wrong drug would be worse than showing none.
     */
    private screenSeq = 0;

    /** Last screened pair, so re-selecting the same drug does not re-issue the GSDD round-trip. */
    private lastScreened: string | null = null;

    mounted() {
        this.screen();
    }

    @Watch("patientId")
    @Watch("drug")
    onInputChanged() {
        this.screen();
    }

    protected async screen() {
        const patientId = this.patientId;
        const drugId = this.drug?.drugId;
        const source = this.drug?.source;

        // Nothing to screen and nothing to say — with no drug or no patient there is no claim to make either
        // way. Bumping the sequence is what abandons a screen still in flight; clearing matches alone is not
        // enough, since the in-flight response would still pass the guard below and repaint an allergy alert
        // for the drug that was just cleared or replaced.
        if (!patientId || !drugId || source === undefined || source === null) {
            this.screenSeq++;
            this.matches = [];
            this.unscreened = false;
            this.lastScreened = null;
            return;
        }

        // NonGSDD products have no GSDD package to screen against, so skip the round-trip that would only
        // report the same thing — but say so, rather than looking identical to a drug that came back clean.
        if (source === DrugSource.NonGsddProduct) {
            this.screenSeq++;
            this.matches = [];
            this.unscreened = true;
            this.lastScreened = null;
            return;
        }

        const key = `${patientId}:${source}:${drugId}`;
        if (key === this.lastScreened) return;

        // Claimed before awaiting, not after: the patient and drug watchers can both fire in the same tick
        // (selecting a patient reloads the drug), and a key set only on completion would let both of those
        // issue their own GSDD round-trip for the very same pair.
        this.lastScreened = key;

        const seq = ++this.screenSeq;
        try {
            const result = await DrugService.screenAllergies(patientId, drugId, source);
            if (seq !== this.screenSeq) return;

            this.matches = result.calculated ? result.matches ?? [] : [];
            this.unscreened = !result.calculated;

            // An un-screened result is not a verdict, so release the key. A GSDD failure comes back as a 200
            // carrying calculated false rather than as a rejection, so it never reaches the catch below, and
            // holding the key would make re-selecting the same drug a no-op for the life of the component.
            if (!result.calculated) this.lastScreened = null;
        } catch (error) {
            if (seq !== this.screenSeq) return;

            // A failed screen is un-screened, not clear. Drop any stale matches but say the drug was never
            // checked; the pharmacist's verification-time DUR remains the backstop. It must not interrupt
            // data entry, so nothing is raised beyond the inline note.
            this.matches = [];
            this.unscreened = true;
            this.lastScreened = null;
            console.error("Error while screening drug allergies", { error });
        }
    }

    protected matchKey(match: AllergyScreeningMatch): string {
        return `${match.allergyType}:${match.matchedOn}:${match.ingredientName ?? ''}`;
    }

    protected describe(match: AllergyScreeningMatch): string {
        const recorded = match.patientAllergyName
            ? `Patient reports an allergy to ${match.patientAllergyName}`
            : `Patient has a recorded allergy matching this ${this.allergyTypeLabel(match.allergyType)}`;

        return match.ingredientName
            ? `${recorded}. The selected compound contains ${match.ingredientName}.`
            : `${recorded}.`;
    }

    protected allergyTypeLabel(allergyType: AllergyType): string {
        switch (allergyType) {
            case AllergyType.Class: return "allergen class";
            case AllergyType.Ingredient: return "ingredient";
            case AllergyType.Drug: return "drug";
            default: return "allergen";
        }
    }
}
</script>

<style scoped>
/* Mirrors the "major" DUR severity treatment from DURListComponent so an allergy conflict reads with the
   same weight here as it does on the verification screen. */
.allergy-warning {
    display: flex;
    align-items: stretch;
    gap: 5px;
    border: 1px solid red;
    border-radius: 3px;
    background: rgba(255, 0, 0, 0.07);
    margin-top: 8px;
}

.allergy-warning-icon {
    background: red;
    color: white;
    padding: 5px 8px;
    display: flex;
    align-items: center;
    font-size: 1.2em;
}

.allergy-warning-body {
    padding: 5px 8px;
}

.allergy-warning-title {
    font-weight: bold;
    color: #b30000;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.allergy-warning-match {
    margin-top: 4px;
}

.allergy-warning-detail {
    font-size: 0.9em;
}

/* Deliberately muted: this is an absence of information, not a finding, and it appears on every NonGSDD
   product. It must never compete with the red conflict alert for attention. */
.allergy-unscreened {
    margin-top: 8px;
    padding: 4px 8px;
    border-left: 3px solid #adb5bd;
    background: rgba(0, 0, 0, 0.03);
    color: #6c757d;
    font-size: 0.9em;
}
</style>
